// Admin Authentication Service
// Secure authentication for admin panel

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: 'super_admin' | 'admin' | 'moderator';
  createdAt: string;
  lastLogin?: string;
}

export interface AuthSession {
  user: AdminUser;
  token: string;
  expiresAt: number;
}

interface LoginAttempt {
  timestamp: number;
  success: boolean;
}

interface AdminAuthState {
  session: AuthSession | null;
  isAuthenticated: boolean;
  loginAttempts: LoginAttempt[];
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  checkSession: () => boolean;
  clearError: () => void;
}

// Simple hash function for demo (in production, use bcrypt on server)
async function hashPassword(password: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(password + 'ochtopus_salt_2024');
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// Generate secure token
function generateToken(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
}

// Default admin credentials (in production, this would be in a secure database)
const ADMIN_CREDENTIALS_KEY = 'bosphorus_admin_credentials';
const DEFAULT_ADMIN_EMAIL = 'admin@bosphoruslabs.com';

// Get or initialize admin credentials
async function getAdminCredentials(): Promise<{ email: string; passwordHash: string }[]> {
  const stored = localStorage.getItem(ADMIN_CREDENTIALS_KEY);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      // Reset if corrupted
    }
  }

  // Initialize with default admin
  const defaultHash = await hashPassword('Bosphorus@2024!');
  const credentials = [
    { email: DEFAULT_ADMIN_EMAIL, passwordHash: defaultHash }
  ];
  localStorage.setItem(ADMIN_CREDENTIALS_KEY, JSON.stringify(credentials));
  return credentials;
}

// Rate limiting constants
const MAX_LOGIN_ATTEMPTS = 5;
const LOCKOUT_DURATION = 15 * 60 * 1000; // 15 minutes
const ATTEMPT_WINDOW = 5 * 60 * 1000; // 5 minutes

export const useAdminAuth = create<AdminAuthState>()(
  persist(
    (set, get) => ({
      session: null,
      isAuthenticated: false,
      loginAttempts: [],
      isLoading: false,
      error: null,

      login: async (email: string, password: string): Promise<boolean> => {
        set({ isLoading: true, error: null });

        try {
          const state = get();
          const now = Date.now();

          // Check rate limiting
          const recentAttempts = state.loginAttempts.filter(
            a => now - a.timestamp < ATTEMPT_WINDOW
          );
          const failedAttempts = recentAttempts.filter(a => !a.success);

          if (failedAttempts.length >= MAX_LOGIN_ATTEMPTS) {
            const oldestFailed = failedAttempts[0];
            const lockoutEnd = oldestFailed.timestamp + LOCKOUT_DURATION;
            if (now < lockoutEnd) {
              const waitMinutes = Math.ceil((lockoutEnd - now) / 60000);
              set({
                isLoading: false,
                error: `Too many failed attempts. Please try again in ${waitMinutes} minutes.`
              });
              return false;
            }
          }

          // Validate credentials
          const credentials = await getAdminCredentials();
          const passwordHash = await hashPassword(password);

          const adminCred = credentials.find(
            c => c.email.toLowerCase() === email.toLowerCase() && c.passwordHash === passwordHash
          );

          if (!adminCred) {
            set({
              isLoading: false,
              error: 'Invalid email or password',
              loginAttempts: [...recentAttempts, { timestamp: now, success: false }]
            });
            return false;
          }

          // Create session
          const token = generateToken();
          const session: AuthSession = {
            user: {
              id: `admin-${Date.now()}`,
              email: adminCred.email,
              name: 'Administrator',
              role: 'super_admin',
              createdAt: new Date().toISOString(),
              lastLogin: new Date().toISOString(),
            },
            token,
            expiresAt: Date.now() + 24 * 60 * 60 * 1000, // 24 hours
          };

          set({
            session,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            loginAttempts: [...recentAttempts, { timestamp: now, success: true }]
          });

          console.log('[AdminAuth] Login successful for:', email);
          return true;
        } catch (error) {
          console.error('[AdminAuth] Login error:', error);
          set({
            isLoading: false,
            error: 'An error occurred during login. Please try again.'
          });
          return false;
        }
      },

      logout: () => {
        set({
          session: null,
          isAuthenticated: false,
          error: null
        });
        console.log('[AdminAuth] Logged out');
      },

      checkSession: (): boolean => {
        const state = get();
        if (!state.session) return false;

        if (Date.now() > state.session.expiresAt) {
          set({
            session: null,
            isAuthenticated: false,
            error: 'Session expired. Please login again.'
          });
          return false;
        }

        return true;
      },

      clearError: () => {
        set({ error: null });
      }
    }),
    {
      name: 'bosphorus-admin-auth',
      partialize: (state) => ({
        session: state.session,
        isAuthenticated: state.isAuthenticated,
        loginAttempts: state.loginAttempts
      })
    }
  )
);

// Change admin password
export async function changeAdminPassword(
  email: string,
  currentPassword: string,
  newPassword: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const credentials = await getAdminCredentials();
    const currentHash = await hashPassword(currentPassword);

    const adminIndex = credentials.findIndex(
      c => c.email.toLowerCase() === email.toLowerCase() && c.passwordHash === currentHash
    );

    if (adminIndex === -1) {
      return { success: false, error: 'Current password is incorrect' };
    }

    // Validate new password
    if (newPassword.length < 8) {
      return { success: false, error: 'Password must be at least 8 characters' };
    }
    if (!/[A-Z]/.test(newPassword)) {
      return { success: false, error: 'Password must contain uppercase letter' };
    }
    if (!/[a-z]/.test(newPassword)) {
      return { success: false, error: 'Password must contain lowercase letter' };
    }
    if (!/[0-9]/.test(newPassword)) {
      return { success: false, error: 'Password must contain a number' };
    }
    if (!/[!@#$%^&*]/.test(newPassword)) {
      return { success: false, error: 'Password must contain a special character (!@#$%^&*)' };
    }

    const newHash = await hashPassword(newPassword);
    credentials[adminIndex].passwordHash = newHash;
    localStorage.setItem(ADMIN_CREDENTIALS_KEY, JSON.stringify(credentials));

    return { success: true };
  } catch (error) {
    console.error('[AdminAuth] Password change error:', error);
    return { success: false, error: 'Failed to change password' };
  }
}

// Add new admin user
export async function addAdminUser(
  email: string,
  password: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const credentials = await getAdminCredentials();

    if (credentials.some(c => c.email.toLowerCase() === email.toLowerCase())) {
      return { success: false, error: 'User already exists' };
    }

    // Validate email
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return { success: false, error: 'Invalid email format' };
    }

    // Validate password
    if (password.length < 8) {
      return { success: false, error: 'Password must be at least 8 characters' };
    }

    const passwordHash = await hashPassword(password);
    credentials.push({ email, passwordHash });
    localStorage.setItem(ADMIN_CREDENTIALS_KEY, JSON.stringify(credentials));

    return { success: true };
  } catch (error) {
    console.error('[AdminAuth] Add user error:', error);
    return { success: false, error: 'Failed to add user' };
  }
}

// Remove admin user
export async function removeAdminUser(email: string): Promise<boolean> {
  try {
    const credentials = await getAdminCredentials();

    // Don't allow removing the last admin
    if (credentials.length <= 1) {
      return false;
    }

    const filtered = credentials.filter(
      c => c.email.toLowerCase() !== email.toLowerCase()
    );
    localStorage.setItem(ADMIN_CREDENTIALS_KEY, JSON.stringify(filtered));

    return true;
  } catch {
    return false;
  }
}

// Protected route component helper
export function requireAuth(): boolean {
  const { isAuthenticated, checkSession } = useAdminAuth.getState();
  return isAuthenticated && checkSession();
}
